import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaClipboardList,
  FaTimes,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const API_BASE = "https://nissi-india.com/api";

const PurchaseReqReport = () => {
  const { isDark, skin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  // =========================================================
  // UI STATE
  // =========================================================

  const [showFilters, setShowFilters] = useState(true);

  // =========================================================
  // FILTER STATE
  // =========================================================

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [reqNo, setReqNo] = useState("");

  const [projectCode, setProjectCode] = useState("");

  const [projectName, setProjectName] = useState("");

  const [store, setStore] = useState("");

  const [status, setStatus] = useState("Waiting");

  const [location, setLocation] = useState("");

  // =========================================================
  // OPTIONS / RESULT STATE
  // =========================================================

  const [storeOptions, setStoreOptions] = useState([]);

  const [projectNameOptions, setProjectNameOptions] =
    useState([]);

  const [projectCodeOptions, setProjectCodeOptions] =
    useState([]);

  const [results, setResults] = useState([]);

  const [loading, setLoading] = useState(false);

  // =========================================================
  // SESSION VALUES
  // =========================================================

  const finYearId =
    localStorage.getItem("finYearId") || "";

  const finYear =
    localStorage.getItem("finYear") || "";

  const branchId =
    localStorage.getItem("branchId") || "";

  const branch = (
    localStorage.getItem("branchType") || ""
  ).toUpperCase();

  const userId =
    localStorage.getItem("userId") || "";

  // =========================================================
  // COMMON HEADERS
  // =========================================================

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    ]
  );

  // =========================================================
  // BRANCH TYPE
  // =========================================================

  const initialBranchType =
    branch === "HO" ? "HO" : "Branch";

  const [branchType, setBranchType] =
    useState(initialBranchType);

  useEffect(() => {
    setBranchType(initialBranchType);

    setLocation(
      initialBranchType === "HO"
        ? "HO"
        : "BRANCH"
    );
  }, [initialBranchType]);

  // =========================================================
  // HELPERS
  // =========================================================

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const [y, m, d] = dateStr.split("-");

    return `${d}/${m}/${y}`;
  };

  const getRowId = (item) =>
    item.fld_PurchaseRequestid ??
    item.fld_purchaserequestid ??
    item.fld_PurchaseRequestID ??
    null;

  const getReqNo = (item) =>
    item.fld_purchaseRequestNo ??
    item.fld_PurchaseRequestNo ??
    item.fld_purchaserequestno ??
    "";

  // =========================================================
  // COMMON UI CLASSES
  // =========================================================

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  // =========================================================
  // BRANCH TYPE CHANGE
  // =========================================================

  const handleBranchTypeChange = (value) => {
    setBranchType(value);

    setLocation(
      value === "HO"
        ? "HO"
        : "BRANCH"
    );
  };

  // =========================================================
  // CLEAR FILTERS
  // =========================================================

  const clearFilters = () => {
    setFromDate("");

    setToDate(
      new Date()
        .toISOString()
        .split("T")[0]
    );

    setReqNo("");

    setProjectCode("");

    setProjectName("");

    setStore("");

    setStatus("Waiting");

    setResults([]);
  };

  // =========================================================
  // PRINT PURCHASE REQUISITION
  // =========================================================

  const handlePrint = (item) => {
    const id = getRowId(item);

    if (!id) {
      toast.error(
        "Purchase Request ID not found"
      );

      return;
    }

    const printUrl =
      `/inventory/Purchase_Print?id=${id}`;

    window.open(
      printUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // =========================================================
  // PRINT ENQUIRY
  // =========================================================

  const handleEnquiryPrint = (item) => {
    const id = getRowId(item);

    if (!id) {
      toast.error(
        "Purchase Request ID not found"
      );

      return;
    }

    const printUrl =
      `/inventory/Purchase_EnquiryPrint?id=${id}`;

    window.open(
      printUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // =========================================================
  // LOAD STORE OPTIONS
  // =========================================================

  useEffect(() => {
    const fetchStoreOptions = async () => {
      try {
        const res = await axios.post(
          `${API_BASE}/getFromStoreOptions`,
          {
            branchtype: branch || "HO",
            branchid: branchId,
          },
          {
            headers: commonHeaders,
          }
        );

        setStoreOptions(
          Array.isArray(
            res.data?.storeOptions
          )
            ? res.data.storeOptions
            : []
        );
      } catch (error) {
        console.error(
          "Store load error:",
          error
        );

        toast.error(
          "Failed to load store options"
        );
      }
    };

    if (branchId) {
      fetchStoreOptions();
    }
  }, [
    branchId,
    branch,
    commonHeaders,
  ]);

  // =========================================================
  // LOAD PROJECT NAMES
  // =========================================================

  useEffect(() => {
    const fetchProjectNames = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/prReport_getProjectName`,
          {
            params: {
              projectId: "Select",
            },

            headers: commonHeaders,
          }
        );

        setProjectNameOptions(
          Array.isArray(res.data?.data)
            ? res.data.data
            : []
        );
      } catch (error) {
        console.error(
          "Project name load error:",
          error
        );

        toast.error(
          "Failed to load project names"
        );
      }
    };

    fetchProjectNames();
  }, [commonHeaders]);

  // =========================================================
  // LOAD PROJECT CODES
  // =========================================================

  useEffect(() => {
    const fetchProjectCodes = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/prReport_getProjectCode`,
          {
            params: {
              projectId: "Select",
            },

            headers: commonHeaders,
          }
        );

        setProjectCodeOptions(
          Array.isArray(res.data?.data)
            ? res.data.data
            : []
        );
      } catch (error) {
        console.error(
          "Project code load error:",
          error
        );

        toast.error(
          "Failed to load project codes"
        );
      }
    };

    fetchProjectCodes();
  }, [commonHeaders]);

  // =========================================================
  // SEARCH
  // =========================================================

  const handleSearch = async () => {
    if (!store) {
      toast.warn(
        "Please select a Store"
      );

      return;
    }

    const params = {
      branchType: location,

      branchId,

      userBranchType:
        location || "HO",

      brType:
        branchType === "HO"
          ? "H"
          : "B",

      reqNo:
        reqNo.trim(),

      fromDate:
        fromDate
          ? formatDate(fromDate)
          : "",

      toDate:
        toDate
          ? formatDate(toDate)
          : "",

      fromStoreId:
        store,

      projectId:
        projectCode ||
        projectName ||
        0,

      status,
    };

    try {
      setLoading(true);

      const res = await axios.get(
        `${API_BASE}/purchaseStoreMaterialList`,
        {
          params,

          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        throw new Error(
          res.data?.message ||
          "Failed to load report"
        );
      }

      const data =
        Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setResults(data);

      toast.success(
        `${
          res.data?.total ??
          data.length
        } requisition(s) loaded`
      );
    } catch (err) {
      console.error(
        "purchaseStoreMaterialList error:",
        err
      );

      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch listing"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // VIEW ALL
  // =========================================================

  const handleViewAll = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${API_BASE}/purchaseStoreMaterialList`,
        {
          params: {
            branchId,

            branchType:
              location || "HO",

            userBranchType:
              location || "HO",

            brType:
              branchType === "HO"
                ? "H"
                : "B",

            reqNo: "",

            fromDate: "",

            toDate: "",

            fromStoreId: "Select",

            projectId: 0,

            status: "Select",
          },

          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        throw new Error(
          res.data?.message ||
          "Failed to load report"
        );
      }

      const data =
        Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setResults(data);

      toast.success(
        `${
          res.data?.total ??
          data.length
        } requisition(s) loaded`
      );
    } catch (err) {
      console.error(
        "View all error:",
        err
      );

      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch all records"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // STATUS STYLE
  // =========================================================

  const statusClass = (value) => {
    if (value === "Approved") {
      return isDark
        ? "bg-green-900/30 text-green-300"
        : "bg-green-100 text-green-700";
    }

    if (
      value === "DisApproved" ||
      value === "Cancelled" ||
      value === "Cancel"
    ) {
      return isDark
        ? "bg-red-900/30 text-red-300"
        : "bg-red-100 text-red-700";
    }

    if (value === "Close") {
      return isDark
        ? "bg-blue-900/30 text-blue-300"
        : "bg-blue-100 text-blue-700";
    }

    return isDark
      ? "bg-yellow-900/30 text-yellow-300"
      : "bg-yellow-100 text-yellow-700";
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <LayoutWrapper>
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Purchase / Store / Material Requisition Report
          </span>
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
        =================================================== */}

        <div
          className={`
            rounded-2xl
            shadow-xl
            border
            overflow-hidden
            ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }
          `}
        >

          {/* FILTER CARD HEADER */}

          <div
            className={`
              px-6
              py-4
              border-b
              flex
              flex-col
              sm:flex-row
              sm:items-center
              justify-between
              gap-4
              ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }
            `}
          >

            <div className="flex items-center gap-3">

              <div
                className={`
                  flex
                  items-center
                  justify-center
                  w-10
                  h-10
                  rounded-xl
                  bg-gradient-to-br
                  from-${baseColor}-500
                  to-${baseColor}-600
                  shadow-md
                `}
              >
                <FaClipboardList className="w-5 h-5 text-white" />
              </div>

              <div>
                <h3
                  className={`
                    text-lg
                    font-bold
                    ${
                      isDark
                        ? "text-white"
                        : "text-slate-900"
                    }
                  `}
                >
                  Purchase Requisition Report
                </h3>

                <p
                  className={`
                    text-xs
                    ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }
                  `}
                >
                  Search purchase, store and material
                  requisition details
                </p>
              </div>
            </div>

            {/* SHOW / HIDE */}

            <button
              type="button"
              onClick={() =>
                setShowFilters(
                  (prev) => !prev
                )
              }
              className={`
                inline-flex
                items-center
                gap-2
                px-3
                py-1.5
                text-sm
                font-medium
                rounded-lg
                border
                transition
                ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }
              `}
            >
              <FaFilter size={14} />

              {showFilters
                ? "Hide Filters"
                : "Show Filters"}
            </button>
          </div>

          {/* =================================================
              FILTER BODY
          ================================================= */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  BRANCH TYPE
              ============================================= */}

              <div className="mb-6">

                <label className={labelClass}>
                  Branch Type
                </label>

                <div className="flex flex-wrap gap-2">

                  {/* HEAD OFFICE */}

                  <button
                    type="button"
                    onClick={() =>
                      handleBranchTypeChange(
                        "HO"
                      )
                    }
                    disabled={
                      branch !== "HO"
                    }
                    className={`
                      px-5
                      py-2.5
                      rounded-xl
                      text-sm
                      font-semibold
                      transition
                      ${
                        branchType === "HO"
                          ? `bg-${baseColor}-${baseShade} text-white shadow`
                          : isDark
                          ? "bg-slate-800 text-slate-300 border border-slate-600"
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                      }
                      ${
                        branch !== "HO"
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }
                    `}
                  >
                    Head Office
                  </button>

                  {/* BRANCH */}

                  <button
                    type="button"
                    onClick={() =>
                      handleBranchTypeChange(
                        "Branch"
                      )
                    }
                    disabled={
                      branch === "HO"
                    }
                    className={`
                      px-5
                      py-2.5
                      rounded-xl
                      text-sm
                      font-semibold
                      transition
                      ${
                        branchType ===
                        "Branch"
                          ? `bg-${baseColor}-${baseShade} text-white shadow`
                          : isDark
                          ? "bg-slate-800 text-slate-300 border border-slate-600"
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                      }
                      ${
                        branch === "HO"
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }
                    `}
                  >
                    Branch
                  </button>
                </div>
              </div>

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div
                className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                "
              >

                {/* FROM DATE */}

                <div>
                  <label className={labelClass}>
                    From Date
                  </label>

                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={inputClass}
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label className={labelClass}>
                    To Date
                  </label>

                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={inputClass}
                  />
                </div>

                {/* REQ NO */}

                <div>
                  <label className={labelClass}>
                    Req No
                  </label>

                  <input
                    type="text"
                    value={reqNo}
                    onChange={(e) =>
                      setReqNo(
                        e.target.value
                      )
                    }
                    placeholder="Enter request number"
                    className={inputClass}
                  />
                </div>

                {/* PROJECT CODE */}

                <div>
                  <label className={labelClass}>
                    Project Code
                  </label>

                  <select
                    value={projectCode}
                    onChange={(e) => {
                      setProjectCode(
                        e.target.value
                      );

                      setProjectName("");
                    }}
                    className={inputClass}
                  >
                    <option value="">
                      Select Project Code
                    </option>

                    {projectCodeOptions.map(
                      (option) => (
                        <option
                          key={option.id}
                          value={option.id}
                        >
                          {option.code}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* PROJECT NAME */}

                <div>
                  <label className={labelClass}>
                    Project Name
                  </label>

                  <select
                    value={projectName}
                    onChange={(e) => {
                      setProjectName(
                        e.target.value
                      );

                      setProjectCode("");
                    }}
                    className={inputClass}
                  >
                    <option value="">
                      Select Project Name
                    </option>

                    {projectNameOptions.map(
                      (option) => (
                        <option
                          key={option.id}
                          value={option.id}
                        >
                          {option.name}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* STORE */}

                <div>
                  <label className={labelClass}>
                    Store{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <select
                    value={store}
                    onChange={(e) =>
                      setStore(
                        e.target.value
                      )
                    }
                    className={inputClass}
                  >
                    <option value="">
                      Select Store
                    </option>

                    {storeOptions.map(
                      (s) => (
                        <option
                          key={
                            s.fld_storeid
                          }
                          value={
                            s.fld_storeid
                          }
                        >
                          {
                            s.fld_storename
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* STATUS */}

                <div>
                  <label className={labelClass}>
                    Status
                  </label>

                  <select
                    value={status}
                    onChange={(e) =>
                      setStatus(
                        e.target.value
                      )
                    }
                    className={inputClass}
                  >
                    <option value="Waiting">
                      Waiting
                    </option>

                    <option value="Cancel">
                      Cancel
                    </option>

                    <option value="Close">
                      Close
                    </option>

                    <option value="Approved">
                      Approved
                    </option>

                    <option value="DisApproved">
                      DisApproved
                    </option>
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION BUTTONS
              ============================================= */}

              <div
                className="
                  flex
                  flex-wrap
                  justify-end
                  gap-3
                  mt-6
                  pt-5
                  border-t
                  border-slate-200
                  dark:border-slate-700
                "
              >

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={clearFilters}
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2.5
                    text-sm
                    font-medium
                    rounded-xl
                    border
                    transition
                    ${
                      isDark
                        ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                        : "border-slate-300 text-slate-700 hover:bg-slate-100"
                    }
                  `}
                >
                  <FaTimes size={14} />

                  Clear Filters
                </button>

                {/* VIEW ALL */}

                <button
                  type="button"
                  onClick={handleViewAll}
                  disabled={loading}
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2.5
                    text-sm
                    font-semibold
                    rounded-xl
                    border
                    transition
                    disabled:opacity-50
                    ${
                      isDark
                        ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                        : "border-slate-300 text-slate-700 hover:bg-slate-100"
                    }
                  `}
                >
                  <FaSearch size={14} />

                  View All
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-5
                    py-2.5
                    text-sm
                    font-semibold
                    text-white
                    rounded-xl
                    shadow
                    transition
                    bg-${baseColor}-600
                    hover:bg-${baseColor}-700
                    disabled:opacity-50
                  `}
                >
                  <FaSearch size={14} />

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`
            rounded-2xl
            shadow-xl
            border
            overflow-hidden
            ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }
          `}
        >

          {/* RESULTS HEADER */}

          <div
            className="
              p-6
              border-b
              border-gray-200
              dark:border-gray-700
            "
          >
            <div
              className="
                flex
                flex-col
                sm:flex-row
                items-start
                sm:items-center
                justify-between
                gap-4
              "
            >

              <div className="flex items-center gap-3">

                <div
                  className={`
                    w-3
                    h-8
                    rounded-full
                    bg-${baseColor}-${baseShade}
                  `}
                />

                <h3 className="text-xl font-bold">
                  Requisition Results
                </h3>

                <span
                  className={`
                    px-3
                    py-1
                    rounded-full
                    text-xs
                    font-semibold
                    ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }
                  `}
                >
                  {results.length} records
                </span>
              </div>
            </div>
          </div>

          {/* =================================================
              LOADING
          ================================================= */}

          {loading ? (
            <div className="py-16 text-center">

              <div
                className={`
                  inline-block
                  w-8
                  h-8
                  border-4
                  border-${baseColor}-200
                  border-t-${baseColor}-600
                  rounded-full
                  animate-spin
                `}
              />

              <p
                className={`
                  mt-3
                  text-sm
                  ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }
                `}
              >
                Loading requisitions...
              </p>
            </div>
          ) : results.length > 0 ? (

            /* ===============================================
               RESULTS TABLE
            =============================================== */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* TABLE HEADER */}

                <thead
                  className={`
                    bg-gradient-to-r
                    from-${baseColor}-600
                    to-${baseColor}-700
                    text-white
                  `}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Sl No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Req No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Type
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Indent Ref
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Request Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Quotation Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Account Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      MD Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      PO Status
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Print
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Enquiry
                    </th>
                  </tr>
                </thead>

                {/* TABLE BODY */}

                <tbody
                  className={`
                    divide-y
                    ${
                      isDark
                        ? "divide-gray-600"
                        : "divide-gray-200"
                    }
                  `}
                >

                  {results.map(
                    (item, idx) => {

                      const requestStatus =
                        item.requeststatus ??
                        item.purchaserequeststatus ??
                        item.reqstatus ??
                        item.purchaseorderstatus ??
                        "Waiting";

                      const quotationStatus =
                        item.quotationstatus ??
                        "Waiting";

                      const accountStatus =
                        item.accountsstatus ??
                        "Waiting";

                      const mdStatus =
                        item.mdstatus ??
                        "Waiting";

                      const poStatus =
                        item.purchaseorderstatus ??
                        "Waiting";

                      return (
                        <tr
                          key={
                            getRowId(item) ||
                            getReqNo(item) ||
                            idx
                          }
                          className={`
                            transition
                            ${
                              isDark
                                ? "bg-gray-800/20 hover:bg-gray-700/30"
                                : "bg-white hover:bg-gray-50"
                            }
                          `}
                        >

                          {/* SL NO */}

                          <td className="px-4 py-3 font-medium">
                            {idx + 1}
                          </td>

                          {/* REQUEST NUMBER */}

                          <td className="px-4 py-3">

                            <span
                              className={`
                                inline-block
                                px-2.5
                                py-1
                                rounded-full
                                text-xs
                                font-semibold
                                ${
                                  isDark
                                    ? "bg-gray-700 text-gray-200"
                                    : `bg-${baseColor}-100 text-${baseColor}-800`
                                }
                              `}
                            >
                              {getReqNo(item)}
                            </span>
                          </td>

                          {/* DATE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.fld_date}
                          </td>

                          {/* TYPE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.fld_type}
                          </td>

                          {/* INDENT */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.fld_indentref}
                          </td>

                          {/* REQUEST STATUS */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`
                                inline-flex
                                px-2.5
                                py-1
                                rounded-full
                                text-xs
                                font-semibold
                                ${statusClass(
                                  requestStatus
                                )}
                              `}
                            >
                              {requestStatus}
                            </span>
                          </td>

                          {/* QUOTATION STATUS */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`
                                inline-flex
                                px-2.5
                                py-1
                                rounded-full
                                text-xs
                                font-semibold
                                ${statusClass(
                                  quotationStatus
                                )}
                              `}
                            >
                              {quotationStatus}
                            </span>
                          </td>

                          {/* ACCOUNT STATUS */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`
                                inline-flex
                                px-2.5
                                py-1
                                rounded-full
                                text-xs
                                font-semibold
                                ${statusClass(
                                  accountStatus
                                )}
                              `}
                            >
                              {accountStatus}
                            </span>
                          </td>

                          {/* MD STATUS */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`
                                inline-flex
                                px-2.5
                                py-1
                                rounded-full
                                text-xs
                                font-semibold
                                ${statusClass(
                                  mdStatus
                                )}
                              `}
                            >
                              {mdStatus}
                            </span>
                          </td>

                          {/* PO STATUS */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`
                                inline-flex
                                px-2.5
                                py-1
                                rounded-full
                                text-xs
                                font-semibold
                                ${statusClass(
                                  poStatus
                                )}
                              `}
                            >
                              {poStatus}
                            </span>
                          </td>

                          {/* PRINT */}

                          <td className="px-4 py-3 text-center">

                            <button
                              type="button"
                              onClick={() =>
                                handlePrint(
                                  item
                                )
                              }
                              className={`
                                p-2
                                rounded-full
                                transition
                                ${
                                  isDark
                                    ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                    : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                                }
                              `}
                              title="Print Requisition"
                            >
                              <FaPrint className="w-4 h-4" />
                            </button>
                          </td>

                          {/* ENQUIRY */}

                          <td className="px-4 py-3 text-center">

                            <button
                              type="button"
                              onClick={() =>
                                handleEnquiryPrint(
                                  item
                                )
                              }
                              className={`
                                p-2
                                rounded-full
                                transition
                                ${
                                  isDark
                                    ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                    : "bg-green-100 text-green-600 hover:bg-green-200"
                                }
                              `}
                              title="Print Enquiry"
                            >
                              <FaPrint className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaClipboardList
                size={40}
                className={`
                  mx-auto
                  mb-3
                  ${
                    isDark
                      ? "text-slate-500"
                      : "text-slate-400"
                  }
                `}
              />

              <p
                className={`
                  text-sm
                  ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }
                `}
              >
                No records found. Use the filters above
                to search for purchase requisitions.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default PurchaseReqReport;